import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Eye, Loader2, Search, Users, XCircle } from "lucide-react";
import closeIcon from "../../assets/cross.svg";
import { certificateService } from "../../services/certificateService";
import { studentService } from "../../services/studentService";

/**
 * Modal for issuing certificates — supports both single-student issuance
 * and "Whole cohort" mode (Class of YYYY) for graduation.
 *
 * Cohort issuance is intentionally GRADUATION-only: graduating an entire
 * class is a real workflow where everyone gets the same cert. Awards and
 * honour rolls usually need per-student titles, so they stay single-mode
 * (we can revisit if schools tell us otherwise).
 *
 * Cohort years are derived client-side from `expectedGraduationDate` on
 * the student roster — set when a school invites a student ("Class of
 * YYYY"). Students without that field show in a "No graduation year set"
 * bucket so they're not silently dropped.
 *
 * Submit:
 *   - Single mode → existing single POST.
 *   - Cohort mode → Promise.allSettled chunked in batches of 8 to stay
 *     under the API throttle ceiling and keep Puppeteer concurrency sane.
 *     onConfirm receives an array of { studentId, result, error } so the
 *     caller can show per-student success/failure.
 */
const CERT_TYPES = [
  { value: "GRADUATION", labelKey: "certificates.types.graduation", default: "Graduation" },
  { value: "HONOUR_ROLL", labelKey: "certificates.types.honourRoll", default: "Honour Roll" },
  { value: "AWARD", labelKey: "certificates.types.award", default: "Award" },
];

const TYPE_DEFAULTS = {
  GRADUATION: {
    title: "Certificate of Graduation",
    body: "In recognition of the successful completion of all academic requirements and demonstrated commitment to excellence.",
  },
  HONOUR_ROLL: {
    title: "Honour Roll Recognition",
    body: "Awarded for outstanding academic achievement and a sustained record of excellence over the school term.",
  },
  AWARD: {
    title: "Certificate of Recognition",
    body: "Presented in recognition of outstanding contribution and achievement.",
  },
};

// Cert type → template key, for looking up the school's saved default text.
const TYPE_TO_TEMPLATE = { GRADUATION: "graduation", HONOUR_ROLL: "honour-roll", AWARD: "award" };

/** Chunk size for parallel batch submission. Below the API throttle
 *  ceiling (100 req / 60s) with headroom, and matches pg-boss's render
 *  batchSize=2 nicely — 8 enqueues land fast, renders fan out at the
 *  worker's natural pace. */
const COHORT_SUBMIT_CHUNK = 8;

const NO_COHORT = "__NO_COHORT__";

export default function IssueCertificateModal({ onClose, onConfirm, onBatchComplete }) {
  const { t } = useTranslation();

  // Form fields
  const [type, setType] = useState("GRADUATION");
  const [mode, setMode] = useState("single"); // 'single' | 'cohort'
  const [studentId, setStudentId] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedCohort, setSelectedCohort] = useState(""); // YYYY string or NO_COHORT
  const [excludedStudentIds, setExcludedStudentIds] = useState(new Set()); // cohort opt-outs
  const [title, setTitle] = useState(TYPE_DEFAULTS.GRADUATION.title);
  const [body, setBody] = useState(TYPE_DEFAULTS.GRADUATION.body);
  // School's saved per-template default text (pre-fills title/body), keyed by
  // template → { title?, body? }. Empty until settings load.
  const [schoolText, setSchoolText] = useState({});
  const [graduationYear, setGraduationYear] = useState(new Date().getFullYear());

  // Data load
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [error, setError] = useState(null);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // { done, total, failures: [] }

  // Preview state — separate from submitting so the admin can preview
  // and submit without one blocking the other.
  const [previewing, setPreviewing] = useState(false);

  // Load students. Larger limit (500) than single-mode-only — we need
  // the full roster to build cohort buckets.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingStudents(true);
      try {
        const result = await studentService.getStudents({ limit: 500, page: 1, status: "ACTIVE" });
        if (cancelled) return;
        setStudents(result?.data || result?.students || result || []);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load students:", err);
          setError(err.message || "Couldn't load student list");
        }
      } finally {
        if (!cancelled) setLoadingStudents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the school's saved default cert text and pre-fill the (initial,
  // GRADUATION) title/body with it — but only while they still match the
  // built-in defaults, so we never clobber edits the admin already made.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await studentService.getSchoolSettings();
        if (cancelled) return;
        const ct = settings?.brandCertText || {};
        setSchoolText(ct);
        const grad = ct.graduation || {};
        if (grad.title?.trim()) setTitle((prev) => (prev === TYPE_DEFAULTS.GRADUATION.title ? grad.title.trim() : prev));
        if (grad.body?.trim()) setBody((prev) => (prev === TYPE_DEFAULTS.GRADUATION.body ? grad.body.trim() : prev));
      } catch {
        // Non-fatal — fall back to the built-in defaults.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Effective default for a type = school override (if set) over the built-in.
  const defaultsFor = (certType) => {
    const override = schoolText[TYPE_TO_TEMPLATE[certType]] || {};
    const base = TYPE_DEFAULTS[certType];
    return {
      title: override.title?.trim() || base.title,
      body: override.body?.trim() || base.body,
    };
  };

  // Type change: swap defaults if user hasn't edited them. Also reset
  // cohort mode if leaving GRADUATION (cohort mode is GRADUATION-only).
  const handleTypeChange = (nextType) => {
    const previousDefaults = defaultsFor(type);
    const nextDefaults = defaultsFor(nextType);
    setType(nextType);
    if (title === previousDefaults.title) setTitle(nextDefaults.title);
    if (body === previousDefaults.body) setBody(nextDefaults.body);
    if (nextType !== "GRADUATION" && mode === "cohort") {
      setMode("single");
    }
  };

  // Derive cohort buckets from the loaded roster. Years are extracted
  // from `expectedGraduationDate`; students without one go into a
  // "No graduation year set" bucket so they remain selectable in single
  // mode but don't accidentally vacuum up into a Class of YYYY batch.
  const cohorts = useMemo(() => {
    const map = new Map(); // key (year string or NO_COHORT) → [students]
    for (const s of students) {
      const date = s.expectedGraduationDate ? new Date(s.expectedGraduationDate) : null;
      const key = date && !isNaN(date.getTime()) ? String(date.getFullYear()) : NO_COHORT;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    // Sort keys: real years descending (newest first), NO_COHORT last.
    const entries = Array.from(map.entries()).sort(([a], [b]) => {
      if (a === NO_COHORT) return 1;
      if (b === NO_COHORT) return -1;
      return Number(b) - Number(a);
    });
    return entries;
  }, [students]);

  // Auto-pick the most recent year as default cohort once data loads.
  useEffect(() => {
    if (!selectedCohort && cohorts.length > 0 && mode === "cohort") {
      const firstRealYear = cohorts.find(([key]) => key !== NO_COHORT);
      if (firstRealYear) {
        setSelectedCohort(firstRealYear[0]);
        // Sync the graduationYear field so it's coherent with the cohort.
        setGraduationYear(firstRealYear[0]);
      }
    }
  }, [cohorts, mode, selectedCohort]);

  // When the cohort changes, sync the graduationYear field.
  useEffect(() => {
    if (mode === "cohort" && selectedCohort && selectedCohort !== NO_COHORT) {
      setGraduationYear(selectedCohort);
    }
  }, [selectedCohort, mode]);

  const cohortStudents = useMemo(() => {
    if (mode !== "cohort" || !selectedCohort) return [];
    const entry = cohorts.find(([key]) => key === selectedCohort);
    return entry ? entry[1] : [];
  }, [cohorts, selectedCohort, mode]);

  const cohortSelectedStudents = useMemo(
    () => cohortStudents.filter((s) => !excludedStudentIds.has(s.id)),
    [cohortStudents, excludedStudentIds],
  );

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const name = `${s.firstName || ""} ${s.lastName || ""}`.toLowerCase();
      const email = (s.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [students, studentSearch]);

  const selectedStudent = students.find((s) => s.id === studentId);

  const canSubmit =
    !submitting &&
    title.trim().length >= 2 &&
    body.trim().length >= 10 &&
    (mode === "single"
      ? Boolean(studentId)
      : selectedCohort && selectedCohort !== NO_COHORT && cohortSelectedStudents.length > 0);

  const buildPayload = (recipientId, graduationYearOverride) => {
    const payload = {
      studentId: recipientId,
      type,
      title: title.trim(),
      bodyText: body.trim(),
    };
    if (type === "GRADUATION") {
      const year = graduationYearOverride ?? graduationYear;
      if (year) payload.graduationYear = Number(year);
    }
    return payload;
  };

  /**
   * Render a watermarked preview using the current form values and open
   * the resulting PDF in a new browser tab. The student name shown is
   * either the currently-selected single recipient or the first member
   * of the cohort batch (so the admin gets a realistic example without
   * generating one PDF per recipient).
   *
   * Network failures surface inline; we don't toast because the preview
   * action is in the modal flow and the user expects feedback locally.
   */
  const handlePreview = async () => {
    setError(null);
    setPreviewing(true);
    try {
      const sampleStudent =
        mode === "single"
          ? selectedStudent
          : cohortSelectedStudents[0] || cohortStudents[0];
      const studentDisplayName = sampleStudent
        ? `${sampleStudent.firstName || ""} ${sampleStudent.lastName || ""}`.trim() ||
          sampleStudent.email
        : undefined;

      const payload = {
        type,
        title: title.trim(),
        bodyText: body.trim(),
        studentDisplayName,
      };
      if (type === "GRADUATION") {
        const year =
          mode === "cohort" && selectedCohort && selectedCohort !== NO_COHORT
            ? Number(selectedCohort)
            : Number(graduationYear);
        if (year) payload.graduationYear = year;
      }

      const blob = await certificateService.previewCertificate(payload);
      const url = URL.createObjectURL(blob);
      // Open in a new tab. Some browsers block window.open from async
      // callbacks if the click trail is lost — we mitigate by firing
      // synchronously after the fetch resolves. If popups are blocked
      // we fall back to triggering a download via an anchor tag.
      const popup = window.open(url, "_blank", "noopener,noreferrer");
      if (!popup) {
        const a = document.createElement("a");
        a.href = url;
        a.download = "certificate-preview.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      // Revoke the object URL after a delay so the popup has time to
      // load it. Don't revoke immediately — the popup hasn't necessarily
      // fetched the blob yet.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(err.message || "Couldn't render preview");
    } finally {
      setPreviewing(false);
    }
  };

  // Preview button is disabled while submitting OR when there's nothing
  // meaningful to preview yet (title/body must have some content; for
  // cohort mode we also need at least one selected recipient).
  const canPreview =
    !submitting &&
    !previewing &&
    title.trim().length >= 2 &&
    body.trim().length >= 10 &&
    (mode === "single" || (selectedCohort && selectedCohort !== NO_COHORT));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);

    try {
      if (mode === "single") {
        await onConfirm(buildPayload(studentId), { mode: "single", count: 1 });
        return;
      }

      // Cohort mode — chunked Promise.allSettled. Track per-student
      // outcomes so the caller can surface partial failures.
      const recipients = cohortSelectedStudents;
      const total = recipients.length;
      const failures = [];
      let done = 0;
      setBatchProgress({ done, total, failures });

      for (let i = 0; i < recipients.length; i += COHORT_SUBMIT_CHUNK) {
        const chunk = recipients.slice(i, i + COHORT_SUBMIT_CHUNK);
        const results = await Promise.allSettled(
          chunk.map((s) => onConfirm(buildPayload(s.id, selectedCohort), { mode: "cohort", count: total, batch: true })),
        );
        results.forEach((res, idx) => {
          if (res.status === "rejected") {
            failures.push({
              student: chunk[idx],
              error: res.reason?.message || String(res.reason),
            });
          }
          done += 1;
        });
        setBatchProgress({ done, total, failures: [...failures] });
      }

      // Signal completion to the parent so it can toast + refresh +
      // decide whether to close. We pass the failure list so the parent
      // can show appropriate severity and keep the modal open on
      // partial failure.
      if (onBatchComplete) {
        await onBatchComplete({ total, failures });
      }

      if (failures.length > 0) {
        setError(
          t("certificates.issueModal.partialFailure", {
            defaultValue: "{{ok}} of {{total}} issued. {{fail}} failed.",
            ok: total - failures.length,
            total,
            fail: failures.length,
          }),
        );
        // Modal stays open so the user sees the partial-failure detail.
      }
    } catch (err) {
      setError(err.message || "Failed to issue certificate");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExclude = (id) => {
    setExcludedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const realCohorts = cohorts.filter(([key]) => key !== NO_COHORT);
  const orphanCount = cohorts.find(([key]) => key === NO_COHORT)?.[1]?.length || 0;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center p-4">
      <div className="w-[680px] max-w-full max-h-[92vh] overflow-y-auto bg-white rounded-[20px] font-['Manrope']">
        {/* Header */}
        <div className="p-6 bg-gradient-to-br from-white to-blue-100 border-b border-blue-100 flex justify-between items-center sticky top-0 z-10">
          <div>
            <h2 className="text-[18px] font-bold text-gray-900">
              {t("certificates.issueModal.title", { defaultValue: "Issue Certificate" })}
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              {mode === "cohort"
                ? t("certificates.issueModal.subtitleCohort", {
                    defaultValue: "Each recipient gets an email and their own cert in their Vault.",
                  })
                : t("certificates.issueModal.subtitle", {
                    defaultValue: "The student gets an email and the cert lands in their Vault.",
                  })}
            </p>
          </div>
          <button onClick={onClose} className="p-1" type="button" disabled={submitting}>
            <img src={closeIcon} alt="Close" className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 bg-white flex flex-col gap-5">
          {/* Type */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              {t("certificates.issueModal.type", { defaultValue: "Type" })}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {CERT_TYPES.map((opt) => {
                const isActive = type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleTypeChange(opt.value)}
                    disabled={submitting}
                    className={`px-4 py-3 rounded-[10px] border-2 text-sm font-semibold transition ${
                      isActive
                        ? "border-primary bg-blue-50 text-primary"
                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {t(opt.labelKey, { defaultValue: opt.default })}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mode toggle — only for GRADUATION */}
          {type === "GRADUATION" && (
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                {t("certificates.issueModal.recipientMode", { defaultValue: "Recipients" })}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <ModeOption
                  active={mode === "single"}
                  onClick={() => setMode("single")}
                  disabled={submitting}
                  title={t("certificates.issueModal.modeSingle", { defaultValue: "One student" })}
                  caption={t("certificates.issueModal.modeSingleCaption", {
                    defaultValue: "Pick a single student to receive this cert.",
                  })}
                />
                <ModeOption
                  active={mode === "cohort"}
                  onClick={() => setMode("cohort")}
                  disabled={submitting}
                  title={t("certificates.issueModal.modeCohort", { defaultValue: "Whole cohort" })}
                  caption={t("certificates.issueModal.modeCohortCaption", {
                    defaultValue: "Issue to everyone in a Class of YYYY at once.",
                  })}
                />
              </div>
            </div>
          )}

          {/* Recipient picker — branches on mode */}
          {mode === "single" ? (
            <SingleRecipientPicker
              students={students}
              filteredStudents={filteredStudents}
              loading={loadingStudents}
              studentSearch={studentSearch}
              setStudentSearch={setStudentSearch}
              studentId={studentId}
              setStudentId={setStudentId}
              selectedStudent={selectedStudent}
              disabled={submitting}
            />
          ) : (
            <CohortRecipientPicker
              loading={loadingStudents}
              realCohorts={realCohorts}
              orphanCount={orphanCount}
              selectedCohort={selectedCohort}
              setSelectedCohort={setSelectedCohort}
              cohortStudents={cohortStudents}
              excludedStudentIds={excludedStudentIds}
              toggleExclude={toggleExclude}
              cohortSelectedStudents={cohortSelectedStudents}
              disabled={submitting}
            />
          )}

          {/* Title */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              {t("certificates.issueModal.titleField", { defaultValue: "Certificate title" })}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              disabled={submitting}
              className="w-full px-3 py-2.5 rounded-[10px] border border-gray-300 text-sm focus:outline-none focus:border-primary disabled:bg-gray-50"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              {t("certificates.issueModal.bodyField", { defaultValue: "Body text" })}
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={600}
              rows={4}
              disabled={submitting}
              className="w-full px-3 py-2.5 rounded-[10px] border border-gray-300 text-sm focus:outline-none focus:border-primary resize-y disabled:bg-gray-50"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{body.length} / 600</p>
          </div>

          {/* Graduation year — only meaningful for GRADUATION single mode.
              In cohort mode it's locked to the selected cohort year and
              shown read-only (the cohort picker already conveys this). */}
          {type === "GRADUATION" && mode === "single" && (
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                {t("certificates.issueModal.graduationYear", { defaultValue: "Graduation year" })}
              </label>
              <input
                type="number"
                value={graduationYear}
                onChange={(e) => setGraduationYear(e.target.value)}
                min={1900}
                max={2200}
                disabled={submitting}
                className="w-32 px-3 py-2.5 rounded-[10px] border border-gray-300 text-sm focus:outline-none focus:border-primary disabled:bg-gray-50"
              />
            </div>
          )}

          {/* Batch progress */}
          {batchProgress && (
            <BatchProgress progress={batchProgress} />
          )}

          {error && (
            <div className="rounded-[10px] bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-between items-center gap-3 bg-white">
          <button
            type="button"
            onClick={handlePreview}
            disabled={!canPreview}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t("certificates.issueModal.previewTooltip", {
              defaultValue: "Open a watermarked PDF preview in a new tab",
            })}
          >
            {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            {previewing
              ? t("certificates.issueModal.previewing", { defaultValue: "Rendering…" })
              : t("certificates.issueModal.preview", { defaultValue: "Preview" })}
          </button>
          <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-5 py-2.5 rounded-[10px] border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t("certificates.issueModal.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] bg-primary text-white text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting
              ? mode === "cohort"
                ? t("certificates.issueModal.issuingBatch", {
                    defaultValue: "Issuing {{done}} of {{total}}…",
                    done: batchProgress?.done ?? 0,
                    total: batchProgress?.total ?? cohortSelectedStudents.length,
                  })
                : t("certificates.issueModal.issuing", { defaultValue: "Issuing…" })
              : mode === "cohort"
                ? t("certificates.issueModal.issueCohortCount", {
                    defaultValue: "Issue {{count}} certificates",
                    count: cohortSelectedStudents.length,
                  })
                : t("certificates.issueModal.issue", { defaultValue: "Issue Certificate" })}
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────

function ModeOption({ active, onClick, disabled, title, caption }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-left px-4 py-3 rounded-[10px] border-2 transition ${
        active
          ? "border-primary bg-blue-50"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className={`text-sm font-bold ${active ? "text-primary" : "text-gray-900"}`}>{title}</div>
      <div className="text-xs text-gray-500 mt-0.5">{caption}</div>
    </button>
  );
}

function SingleRecipientPicker({
  students,
  filteredStudents,
  loading,
  studentSearch,
  setStudentSearch,
  studentId,
  setStudentId,
  selectedStudent,
  disabled,
}) {
  const { t } = useTranslation();
  return (
    <div>
      <label className="text-sm font-semibold text-gray-700 mb-2 block">
        {t("certificates.issueModal.student", { defaultValue: "Recipient" })}
      </label>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={studentSearch}
          onChange={(e) => setStudentSearch(e.target.value)}
          placeholder={t("certificates.issueModal.studentSearch", {
            defaultValue: "Search by name or email...",
          })}
          disabled={disabled || loading}
          className="w-full pl-9 pr-3 py-2.5 rounded-[10px] border border-gray-300 text-sm focus:outline-none focus:border-primary disabled:bg-gray-50"
        />
      </div>
      <div className="mt-2 max-h-[180px] overflow-y-auto rounded-[10px] border border-gray-200 bg-white">
        {loading ? (
          <div className="p-4 text-sm text-gray-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" />
            {t("certificates.issueModal.loadingStudents", { defaultValue: "Loading students…" })}
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">
            {t("certificates.issueModal.noStudents", { defaultValue: "No students match." })}
          </div>
        ) : (
          filteredStudents.map((s) => {
            const name = `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.email;
            const isSelected = s.id === studentId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setStudentId(s.id)}
                disabled={disabled}
                className={`w-full text-left px-3 py-2 flex justify-between items-center border-b border-gray-100 last:border-b-0 hover:bg-blue-50 transition ${
                  isSelected ? "bg-blue-50" : ""
                }`}
              >
                <div>
                  <div className="text-sm font-medium text-gray-900">{name}</div>
                  <div className="text-xs text-gray-500">{s.email}</div>
                </div>
                {isSelected && (
                  <span className="text-xs font-semibold text-primary">
                    {t("certificates.issueModal.selected", { defaultValue: "Selected" })}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
      {selectedStudent && (
        <p className="text-xs text-gray-500 mt-1.5">
          {t("certificates.issueModal.willIssueTo", {
            defaultValue: "Will issue to: {{name}}",
            name: `${selectedStudent.firstName || ""} ${selectedStudent.lastName || ""}`.trim() || selectedStudent.email,
          })}
        </p>
      )}
    </div>
  );
}

function CohortRecipientPicker({
  loading,
  realCohorts,
  orphanCount,
  selectedCohort,
  setSelectedCohort,
  cohortStudents,
  excludedStudentIds,
  toggleExclude,
  cohortSelectedStudents,
  disabled,
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="rounded-[10px] border border-gray-200 bg-white p-4 text-sm text-gray-500 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" />
        {t("certificates.issueModal.loadingStudents", { defaultValue: "Loading students…" })}
      </div>
    );
  }

  if (realCohorts.length === 0) {
    return (
      <div className="rounded-[10px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <div className="font-semibold mb-1">
          {t("certificates.issueModal.noCohorts", { defaultValue: "No graduation years on file" })}
        </div>
        <p className="text-amber-700">
          {t("certificates.issueModal.noCohortsHint", {
            defaultValue:
              "Cohort issuance derives Class of YYYY from each student's expected graduation date. Set graduation dates on invite, or use One student mode instead.",
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className="text-sm font-semibold text-gray-700 mb-2 block">
          {t("certificates.issueModal.cohort", { defaultValue: "Cohort" })}
        </label>
        <select
          value={selectedCohort}
          onChange={(e) => setSelectedCohort(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2.5 rounded-[10px] border border-gray-300 text-sm focus:outline-none focus:border-primary bg-white disabled:bg-gray-50"
        >
          {realCohorts.map(([year, list]) => (
            <option key={year} value={year}>
              {t("certificates.issueModal.cohortOption", {
                defaultValue: "Class of {{year}} ({{count}})",
                year,
                count: list.length,
              })}
            </option>
          ))}
        </select>
        {orphanCount > 0 && (
          <p className="text-xs text-gray-500 mt-1.5">
            {t("certificates.issueModal.orphanHint", {
              defaultValue: "{{count}} student(s) have no graduation year on file and aren't shown here.",
              count: orphanCount,
            })}
          </p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-700">
            {t("certificates.issueModal.recipients", { defaultValue: "Recipients" })}
          </label>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
            <Users size={12} />
            {t("certificates.issueModal.recipientsCount", {
              defaultValue: "{{selected}} of {{total}} selected",
              selected: cohortSelectedStudents.length,
              total: cohortStudents.length,
            })}
          </span>
        </div>
        <div className="max-h-[200px] overflow-y-auto rounded-[10px] border border-gray-200 bg-white">
          {cohortStudents.map((s) => {
            const name = `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.email;
            const isExcluded = excludedStudentIds.has(s.id);
            return (
              <label
                key={s.id}
                className={`flex items-center gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 transition ${
                  isExcluded ? "opacity-50" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={!isExcluded}
                  onChange={() => toggleExclude(s.id)}
                  disabled={disabled}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{name}</div>
                  <div className="text-xs text-gray-500">{s.email}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BatchProgress({ progress }) {
  const { t } = useTranslation();
  const { done, total, failures } = progress;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const successCount = done - failures.length;
  return (
    <div className="rounded-[10px] border border-blue-200 bg-blue-50/60 px-4 py-3">
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold text-gray-900">
          {t("certificates.issueModal.batchProgress", {
            defaultValue: "Progress: {{done}} of {{total}}",
            done,
            total,
          })}
        </span>
        <span className="text-xs text-gray-500">{pct}%</span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-blue-100 overflow-hidden">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-xs">
        <span className="inline-flex items-center gap-1 text-emerald-700">
          <CheckCircle2 size={12} />
          {t("certificates.issueModal.batchOk", { defaultValue: "{{count}} succeeded", count: successCount })}
        </span>
        {failures.length > 0 && (
          <span className="inline-flex items-center gap-1 text-red-700">
            <XCircle size={12} />
            {t("certificates.issueModal.batchFail", { defaultValue: "{{count}} failed", count: failures.length })}
          </span>
        )}
      </div>
      {failures.length > 0 && (
        <details className="mt-2 text-xs text-red-700">
          <summary className="cursor-pointer font-semibold">
            {t("certificates.issueModal.batchFailDetails", { defaultValue: "Show failures" })}
          </summary>
          <ul className="mt-1 list-disc pl-5 space-y-0.5">
            {failures.map((f, i) => (
              <li key={i}>
                <span className="font-medium">
                  {`${f.student.firstName || ""} ${f.student.lastName || ""}`.trim() || f.student.email}
                </span>
                {" — "}
                {f.error}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
